import React, { useState } from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import type { UserInfo } from '@wix/cli-auth';
import { Box, Spinner, Text, useAsync } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import { getRepoType, createPackageManager } from '@wix/package-manager';
import { WelcomeMessage } from './WelcomeMessage.js';
import { validateForLatestVersion } from '../validations/index.js';
import pkgJson from '../../package.json' with { type: 'json' };
import type { GenerationData } from '../types.js';
import type { CommandOptions } from '../parse-command-options.js';
import { AuthProvider } from './AuthProvider.js';
import { Questions } from './Questions/Questions.js';
import { getTasks } from '../tasks.js';
import type { TaskData } from '../tasks.js';
import { FinishedSuccessfullyMessage } from './FinishedSuccessfullyMessage.js';
import { TaskList } from './TaskList.js';
import { useTaskRunner } from '../task-runner.js';
import type { TaskStatus } from '../task-runner.js';
import { McpConfigCreated } from './mcp/McpConfigCreated.js';

const GenerationProgress: FC<{
  tasks: TaskStatus[];
  totalTaskCount: number;
}> = ({ tasks, totalTaskCount }) => {
  const { t } = useTranslation();

  return (
    <>
      <Box marginBottom={1} marginTop={1}>
        <Text>{t('create_app.generate_project.generating_project')}</Text>
      </Box>

      <Box marginBottom={1} flexDirection="column">
        <TaskList tasks={tasks} totalTaskCount={totalTaskCount} />
      </Box>
    </>
  );
};

export const CreateAppCommand: FC<{
  userInfo: UserInfo | null;
  targetParentFolder: string;
  commandOptions: CommandOptions;
}> = ({ userInfo, targetParentFolder, commandOptions }) => {
  const { t } = useTranslation();

  const { execute: executeTaskRunner, status: taskRunnerStatus } =
    useTaskRunner();
  const [generationData, setGenerationData] = useState<GenerationData>();

  const { status } = useAsync(async () => {
    const repoType = await getRepoType(targetParentFolder);
    const packageManager = await createPackageManager(repoType);

    await validateForLatestVersion(pkgJson, packageManager);

    return { repoType, packageManager };
  }, []);

  async function runTasks(data: TaskData) {
    setGenerationData(data);
    const tasks = getTasks(data, t);
    await executeTaskRunner(tasks);
  }

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text={t('general.loading')} />,
    Success: ({ result: { repoType, packageManager } }) => {
      return (
        <Box
          flexDirection="column"
          borderStyle="single"
          borderColor="gray"
          borderBottom={false}
          borderRight={false}
          borderTop={false}
          paddingLeft={1}
        >
          <WelcomeMessage />

          <AuthProvider userInfo={userInfo}>
            <Questions
              repoType={repoType}
              commandOptions={commandOptions}
              targetParentFolder={targetParentFolder}
              onSubmit={(generationData) =>
                runTasks({
                  ...generationData,
                  repoType,
                  packageManager,
                  targetParentFolder,
                  skipInstall: commandOptions.skipInstall,
                  skipGit: commandOptions.skipGit,
                  templateParams: commandOptions.templateParams,
                })
              }
            />
          </AuthProvider>

          {generationData &&
            match(taskRunnerStatus, {
              Idle: () => null,
              Running: (status) => <GenerationProgress {...status} />,
              Done: (status) => (
                <>
                  <GenerationProgress {...status} />
                  {match(generationData.mcpConfigResult, {
                    Created: ({ mcpPath, docsUrl }) => (
                      <McpConfigCreated mcpPath={mcpPath} docsUrl={docsUrl} />
                    ),
                    Canceled: () => null,
                  })}
                  <FinishedSuccessfullyMessage
                    packageName={generationData.packageName}
                    appData={generationData.appData}
                    packageManager={packageManager}
                  />
                </>
              ),
            })}
        </Box>
      );
    },
  });
};
